import { useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "components/loading/LoadingSpinner";
import { SearchInput } from "components/input/SearchInput";
import { Button } from "components/button";
import { SearchType } from "hooks/search/types";
import { useSearch } from "hooks/search/useSearch";
import { useFilters } from "hooks/search/useFilters";
import { BrowseThemaCodesDialog } from "./BrowseThemaCodesDialog";
import {
  getThemaCodeKind,
  sortThemaCodeItems,
  ThemaCodeFilterSection,
} from "./ThemaCodeFilterSection";
import { MAX_SELECT_ALL_ITEMS } from "./constants";

interface ThemaCodeFilterProps {
  name: string;
  searchQuery?: string;
  searchType?: SearchType;
}

export function ThemaCodeFilter({
  name,
  searchQuery = "",
  searchType = SearchType.QUICK,
}: ThemaCodeFilterProps) {
  const { register, setValue } = useFormContext();
  const { t } = useTranslation(["books"]);
  const { filters } = useFilters();
  const [browseOpen, setBrowseOpen] = useState(false);
  const searchInput = useWatch({ name: `filters.${name}.search` });
  const activeFilters = useWatch({ name: "filters" });
  const watchedValues = useWatch({ name: `filters.${name}.values` });
  const selectedValues: string[] = useMemo(
    () => watchedValues ?? [],
    [watchedValues],
  );
  const filterConfig = filters.find((filter) => filter.name === name);

  const { data: searchData, isFetching: isLoadingSearch } = useSearch(
    {
      query: searchQuery,
      type: searchType,
      facets: [name],
      facetSearch: searchInput ? { field: name, text: searchInput } : undefined,
      facetSize: 5000,
      pageSize: 0,
      filters: activeFilters,
    },
    { debounce: 250 },
  );

  const items = useMemo(() => {
    const counts =
      searchData?.facets?.find((facet) => facet.field === name)?.counts ?? [];
    return sortThemaCodeItems(counts);
  }, [searchData, name]);

  const metadataByValue = useMemo(
    () => new Map(items.map((item) => [item.value, item])),
    [items],
  );

  const selectedItems = sortThemaCodeItems(
    selectedValues.map(
      (value) =>
        metadataByValue.get(value) ?? {
          value,
          label: value,
          kind: getThemaCodeKind(value),
        },
    ),
  );

  const suggestionItems = items.filter(
    (item) => !selectedValues.includes(item.value),
  );

  const selectAllDisabled = items.length > MAX_SELECT_ALL_ITEMS;
  const allSelected =
    items.length > 0 &&
    items.every((item) => selectedValues.includes(item.value));

  const handleToggle = (value: string, checked: boolean) => {
    const nextValues = checked
      ? Array.from(new Set([...selectedValues, value]))
      : selectedValues.filter((selectedValue) => selectedValue !== value);
    setValue(`filters.${name}.values`, nextValues);
  };

  return (
    <div className="flex flex-col gap-y-2">
      <div className="px-6">
        <Button
          variant="link"
          icon="search"
          className="p-0 text-primary-600 hover:text-primary-700"
          onClick={() => setBrowseOpen(true)}
        >
          {t("books:filters.themaCodes.browse")}
        </Button>
      </div>
      <SearchInput
        className="mx-6 mb-2 w-[calc(100%-3rem)]"
        placeholder={filterConfig?.placeholder}
        {...register(`filters.${name}.search`)}
      />
      <ThemaCodeFilterSection
        items={selectedItems}
        name={name}
        label={t("books:filters.multiSelect.selected")}
        actionLabel={
          selectedValues.length
            ? t("books:filters.multiSelect.deselectAll")
            : undefined
        }
        selectedValues={selectedValues}
        onAction={() => setValue(`filters.${name}.values`, [])}
        onToggle={handleToggle}
      />
      {isLoadingSearch ? (
        <div className="flex w-full justify-center py-2 px-6 text-sm">
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <ThemaCodeFilterSection
          items={suggestionItems}
          name={name}
          label={t("books:filters.multiSelect.suggestions")}
          actionLabel={
            allSelected ? undefined : t("books:filters.multiSelect.selectAll")
          }
          actionDisabled={selectAllDisabled}
          actionDisabledTooltip={t(
            "books:filters.multiSelect.selectAllDisabled",
          )}
          selectedValues={selectedValues}
          noSuggestionsFound={
            !suggestionItems.length
              ? t("books:filters.multiSelect.noSuggestionsFound")
              : undefined
          }
          onAction={() => {
            if (!selectAllDisabled) {
              setValue(
                `filters.${name}.values`,
                items.map((item) => item.value),
              );
            }
          }}
          onToggle={handleToggle}
        />
      )}
      <BrowseThemaCodesDialog open={browseOpen} onOpenChange={setBrowseOpen} />
    </div>
  );
}
